import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { lastAbsenceDateError } from '../claimRules'
import { useState, useEffect } from 'react'

import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import useAuth from '../../../../hooks/useAuth'
import moment from 'moment'

import EditClaimConfirmation from '../EditClaimConfirmation'
import StepOneEdit from '../Partials/StepOneEdit'
import StepTwoEdit from '../Partials/StepTwoEdit'
import StepThree from '../Partials/StepThree'
import ClaimLoader from './ClaimLoader'

const EditClaim = ({ open, claim = {}, policy = {}, onRefresh, onClaimLoaded }) => {
  const axios = useAxiosPrivate()
  const { createInfo } = useAuth()
  const [step, setStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isFetchingClaimData, setIsFetchingClaimData] = useState(true)
  const [claimLoadError, setClaimLoadError] = useState('')
  const [isStepLoading, setIsStepLoading] = useState(true)
  const [isStepOneValidState, setIsStepOneValidState] = useState(false)
  const [isStepTwoValidState, setIsStepTwoValidState] = useState(false)
  const [formData, setFormData] = useState({
    stepOne: {},
    stepTwo: {},
    stepThree: {},
  })

  const [declaration, setDeclaration] = useState('');
  const [department, setDepartment] = useState(null)

  const endDateError = lastAbsenceDateError(formData.stepOne);
  const nextStep = () => {
    setIsStepLoading(step === 1);
    setStep((prev) => Math.min(prev + 1, 4));
  };

  const resetClaimEditor = () => {
    setStep(1)
    setFormData({
      stepOne: {},
      stepTwo: {},
      stepThree: {},
    });
    setIsStepOneValidState(false)
    setIsStepTwoValidState(false)
    setLoading(false)
  }

  useEffect(() => {
    if (!open) {
      resetClaimEditor()
    }
  }, [open])

  useEffect(() => {
    let active = true;
    const fetchClaimData = async () => {
      if (!claim?.claim_id || !policy?.policy_id) return
      if (formData?.stepOne?.claim_id === claim.claim_id) return
      if (formData?.stepTwo?.claim_id === claim.claim_id) return

      setIsFetchingClaimData(true);
      setClaimLoadError('');
      try {
        const res = await axios.get(`/v1/staff-hub/claims/current/${policy.policy_id}/${claim.claim_id}?api_token=${process.env.REACT_APP_KIS_TOKEN}`);
        if (!active) return;
        const current = res.data;
        if (onClaimLoaded) onClaimLoaded(current.claim)
        const {
          claim_absence_start_date,
          claim_absence_end_date,
          claim_phased,
          confidential,
          claim_information,
          condition_id,
          condition,
          staff,
          declaration_signed,
          conditions
        } = current.claim;

        const parsedClaimInfo = JSON.parse(claim_information || '{}');

        setFormData({
          stepOne: {
            selectedStaff: staff || {},
            selectedCondition: {
              condition_id: conditions?.condition_id || condition_id,
              condition_title: conditions?.condition_title || condition,
              condition_phased: conditions?.condition_phased ?? 0,
            },
            absenceStart: claim_absence_start_date || null,
            absenceEnd: claim_absence_end_date || null,
            isPhasedReturn: !!claim_phased,
            isConfidential: !!confidential,
            claim_id: current.claim.claim_id
          },
          stepTwo: {
            model: parsedClaimInfo,
          },
          stepThree: {
            accepted: !!declaration_signed,
          },
        })
      } catch (err) {
        if (!active) return;
        setClaimLoadError('Unable to load this claim. Please reload the page to try again.');
        console.error("Failed to load current claim data", err);
      } finally {
        if (active) setIsFetchingClaimData(false);
      }
    };

    fetchClaimData();
    return () => { active = false; };
  }, [claim?.claim_id, policy?.policy_id]);

  useEffect(() => {
    const data = formData.stepOne;
    const isValid = checkStepOneValidity(data);
    setIsStepOneValidState(isValid);
  }, [
    formData.stepOne.selectedStaff,
    formData.stepOne.selectedCondition,
    formData.stepOne.isPhasedReturn,
    formData.stepOne.absenceStart,
    formData.stepOne.absenceEnd
  ]);

  useEffect(() => {
    const storedPolicy = JSON.parse(localStorage.getItem('policyData'))
    setDepartment(storedPolicy.department)
  }, [])

  useEffect(() => {
    if(!department) return

    let active = true
    setDeclaration('')

    const getDeclaration = async () => {
      try {
        const response = await axios.get(`v1/staff-hub/declaration/${department}`)

        if (active) {
          setDeclaration(response.data.data || '')
        }
      } catch (error) {
        console.error('Failed to get step 3 declaration', error)
      }
    }

    getDeclaration()

    return () => {
      active = false
    }
  }, [axios, department])

  const checkStepOneValidity = (data) => {
    if (!data || !data.selectedCondition || !data.absenceStart) return false;
    if (lastAbsenceDateError(data)) return false;

    const { selectedStaff, selectedCondition, absenceStart, absenceEnd } = data;

    if (!selectedStaff.staff_id || !selectedCondition.condition_id) return false;
    if (!selectedStaff.stress && selectedCondition.stress === 0) return false;

    if (
      !selectedStaff.maternityCover &&
      selectedCondition.condition_id === 'e3afcdea-7b54-11e7-a963-336a7d328401'
    ) return false;

    const start = moment(absenceStart)
    const end = absenceEnd ? moment(absenceEnd) : null
    const today = moment()
    const futureClaimsAllowed = selectedCondition.future_claims === 1;

    if (start.isAfter(today) && !futureClaimsAllowed) return false;

    if (end) {
      if (end.isBefore(start)) return false;
      if (end.isAfter(today) && !futureClaimsAllowed) return false;
    }

    if (end) {
      if (end.isBefore(start)) return false
      if (end.isAfter(today) && selectedCondition.future_claims !== 1) return false
    }

    return true
  }

  const isStepTwoValid = () => {
    return isStepTwoValidState
  }

  const isStepThreeValid = () => {
    const data = formData.stepThree;
    return data?.accepted === true;
  }

  const isCurrentStepValid = () => {
    switch (step) {
      case 1:
        return isStepOneValidState
      case 2:
        return isStepTwoValid();
      case 3:
        return isStepThreeValid();
      default:
        return false;
    }
  }

  const handleNext = async () => {
    if (!isCurrentStepValid()) return

    setLoading(true)

    try {
      switch (step) {
        case 1:
          await submitStepOne()
          break;
        case 2:
          await submitStepTwo()
          break;
        case 3:
          await submitStepThree()
          break;
        default:
          break;
      }
    } finally {
      setLoading(false)
    }
  }

  const handleStepTwoUpdate = (stepTwoUpdate) => {
    setFormData(prev => ({
      ...prev,
      stepTwo: {
        ...prev.stepTwo,
        model: {
          ...prev.stepTwo.model,
          ...stepTwoUpdate.model,
        },
      },
    }))
  }

  const submitStepOne = async () => {
    if (lastAbsenceDateError(formData.stepOne)) return;
    const {
      selectedStaff,
      selectedCondition,
      absenceStart,
      absenceEnd,
      isPhasedReturn,
      isConfidential,
    } = formData.stepOne;

    const claimId = formData.stepOne.claim_id || claim?.claim_id;

    if (!selectedStaff?.staff_id || !selectedCondition?.condition_id || !absenceStart) {
      createInfo('error', 'Missing required fields for Step One submission.')
      return;
    }

    const payload = {
      staff_id: selectedStaff.staff_id,
      claim_id: claimId || 0,
      condition_id: selectedCondition.condition_id,
      policy_id: policy.policy_id,
      claim_absence_start_date: absenceStart,
      claim_absence_end_date: absenceEnd || null,
      claim_phased: isPhasedReturn ? 1 : 0,
      confidential: isConfidential ? 1 : 0
    }

    try {
      if (claim?.claim_id) {
        createInfo('success', 'Claim updated successfully.')
        nextStep()
      } else {
        const res = await axios.post('/api/claims', payload)
        if (res?.data?.claim_id) {
          const newClaim = res.data

          setFormData((prev) => ({
            ...prev,
            stepOne: {
              ...prev.stepOne,
              claim_id: newClaim.claim_id
            }
          }))

          createInfo('success', 'Claim created successfully.')
          nextStep();
        } else {
          createInfo('error', 'Claim created, but no claim id returned.')
        }
        nextStep();
      }
    } catch (err) {
      console.error("Step One error:", err)
      createInfo('error', 'Failed to submit Step One.')
    }
  }

  const submitStepTwo = async () => {
    const claimId = claim?.claim_id || formData?.stepOne?.claim_id
    const stepTwo = formData?.stepTwo

    if (!claimId || !stepTwo) {
      console.warn("Missing claim ID or step two data.")
      return
    }

    const {
      model = {}
    } = stepTwo;

    if (!isStepTwoValid()) {
      console.warn("Incomplete Step Two data.")
      return
    }

    const claim_information = JSON.stringify(model)

    const payload = {
      claim_information,
      naht_member: "",
    }

    try {
      const res = await axios.post(`/api/v1/staff-hub/claims/update/${claimId}?api_token=${process.env.REACT_APP_KIS_TOKEN}`, payload)
      if (res.status === 200 || res.status === 201 || res.status === 204) {
        createInfo('success', 'Step Two successfully saved.')
        nextStep()
      } else {
        createInfo('error', 'Unexpected response while submitting Step Two.')
      }
    } catch (err) {
      createInfo('error', 'Failed to submit Step Two')
    }
  }

  const submitStepThree = async () => {
    const claimId = claim?.claim_id || formData?.stepOne?.claim_id

    if (!claimId) {
      createInfo('warning', 'No claim ID available to calculate the claim.')
      return
    }

    try {
      setSubmitting(true)
      const res = await axios.get(`/api/v1/staff-hub/claims/calculate/${claimId}?api_token=${process.env.REACT_APP_KIS_TOKEN}`)
      if (res.status === 200) {
        createInfo('success', 'Claim successfully calculated.')
        nextStep()
        if (onRefresh) onRefresh()
      } else {
        createInfo('error', 'Unexpected response from calculate endpoint.')
      }
    } catch (err) {
      createInfo('error', 'Failed to calculate claim')
    }
    setSubmitting(false)
  }

  if (isFetchingClaimData) return <ClaimLoader />;
  if (claimLoadError) return <div role="alert" className="p-6 text-red-600">{claimLoadError}</div>;

  return (
    <div className="p-6">
      {step >= 1 && step <= 3 && (
        <>
          {isFetchingClaimData && (
            <div className="mb-4 inline-flex items-center gap-2 text-lg text-sas">
              <span>Loading...</span>
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
          )}
          {step === 1 && (
            <StepOneEdit
              onLoadingChange={setIsStepLoading}
              errors={{ endDate: endDateError }}
              claim={claim}
              policyId={policy?.policy_id}
              data={formData.stepOne}
              onUpdate={(updated) =>
                setFormData((prev) => ({
                  ...prev,
                  stepOne: { ...prev.stepOne, ...updated },
                }))
              }
            />
          )}
          {step === 2 && (
            <StepTwoEdit
              onLoadingChange={setIsStepLoading}
              onValidationChange={setIsStepTwoValidState}
              key={claim?.claim_id}
              claim={claim}
              policy={policy}
              data={formData.stepTwo}
              conditionId={formData.stepOne?.selectedCondition?.condition_id || claim?.condition_id}
              onUpdate={handleStepTwoUpdate}
            />
          )}
          {step === 3 && (
            <StepThree
              data={formData.stepThree}
              declaration={declaration}
              onUpdate={(data) =>
                setFormData((prev) => ({ ...prev, stepThree: data }))
              }
            />
          )}
          <div className={`flex justify-between mt-8 ${isStepLoading ? 'hidden' : ''}`}>
            {step < 3 ? (
              loading ? (
                <button
                  disabled
                  className="px-4 py-2 text-sm font-medium text-white bg-sas rounded-md flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2 text-lg" />
                  Loading...
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!isCurrentStepValid() || loading || submitting || isFetchingClaimData}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md ${(isCurrentStepValid() && !loading && !submitting && !isFetchingClaimData) ? 'bg-sas hover:bg-sas-dark' : 'bg-gray-400 cursor-not-allowed'
                    }`}
                >
                  Next Step
                </button>
              )
            ) : (
              <>
                {formData.stepThree?.accepted && (
                  <button onClick={submitStepThree} disabled={submitting || isFetchingClaimData} className={`px-4 py-2 text-sm font-medium text-white rounded-md ${(submitting || isFetchingClaimData) ? 'bg-gray-400 cursor-not-allowed' : 'bg-sas hover:bg-sas-dark'}`}>
                    {submitting ? (
                      <span className="inline-flex items-center gap-2 text-lg">
                        Loading...
                        <FontAwesomeIcon icon={faSpinner} spin />
                      </span>
                    ) : (
                      'Submit'
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}
      {step === 4 && <EditClaimConfirmation department={policy.department} />}
    </div>
  )
}

export default EditClaim
